import { useState, useEffect, useMemo } from 'react'; // React削除
import { format } from 'date-fns';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Trash2, AlertCircle } from 'lucide-react';
import ExportPanel from '../components/ExportPanel';
import { useAnalysisDataStore } from '../stores/analysisDataStore';
import { useSpeedAnalysisStore } from '../stores/speedAnalysisStore';
import { usePitchAnalysisStore } from '../stores/pitchAnalysisStore';
import { useContactAnalysisStore } from '../stores/contactAnalysisStore';
import { cn } from '../utils/cn';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

ChartJS.defaults.animation = false;
ChartJS.defaults.responsive = true;
ChartJS.defaults.maintainAspectRatio = false;

const MAX_RESULTS = 20;

const SettingsPage = () => {
  const { results, isLoading, error, fetchResults, addResult, deleteResult, isOverLimit, getResultCount } = useAnalysisDataStore();
  const speedResults = useSpeedAnalysisStore(state => state.calculateResults());
  const pitchResults = usePitchAnalysisStore(state => state.calculateResults());
  const contactResults = useContactAnalysisStore(state => ({
    contactTime: state.contactTime,
    flightTime: state.flightTime
  }));

  const [name, setName] = useState('');
  const [distance, setDistance] = useState('');
  const [comment, setComment] = useState('');
  const [measurementDate, setMeasurementDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resultCount, setResultCount] = useState(0);

  useEffect(() => {
    fetchResults();
    updateResultCount();
  }, [fetchResults]);

  const updateResultCount = async () => {
    const count = await getResultCount();
    setResultCount(count);
  };

  useEffect(() => {
    if (speedResults) {
      setDistance(speedResults.distance.toString());
    }
  }, [speedResults]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    
    if (!name || !distance) {
      setSaveError('名前と距離を入力してください');
      return;
    }

    try {
      // Check if we're at the limit
      if (resultCount >= MAX_RESULTS) {
        setSaveError(`データの保存数が上限（${MAX_RESULTS}件）に達しています。古いデータを削除してから再度お試しください。`);
        return;
      }

      // 98行目付近の修正：null → undefined に変換
      const combinedData = {
        speed: speedResults?.speed,
        pitch: pitchResults?.pitch,
        stride: pitchResults?.stride,
        contactTime: contactResults.contactTime ?? undefined, // null → undefined
        flightTime: contactResults.flightTime ?? undefined,   // null → undefined
        predicted100m: speedResults ? (100 / speedResults.speed).toFixed(2) : undefined
      };

      await addResult({
        measurement_date: measurementDate,
        name,
        distance: Number(distance),
        data: combinedData,
        comment
      });

      setName('');
      setDistance('');
      setComment('');
      setMeasurementDate(format(new Date(), 'yyyy-MM-dd'));
      
      // Update the count after adding
      updateResultCount();
    } catch (err) {
      console.error('Failed to save:', err);
      if (err instanceof Error) {
        setSaveError(err.message);
      } else {
        setSaveError('データの保存に失敗しました');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('このデータを削除してもよろしいですか？')) {
      await deleteResult(id);
      // Update the count after deleting
      updateResultCount();
    }
  };

  const handleExportCSV = () => {
    const headers = [
      '日付',
      '名前',
      'スピード (m/s)',
      'ピッチ (歩/秒)',
      'ストライド (m)',
      '接地時間 (秒)',
      '滞空時間 (秒)',
      '100m予測 (秒)',
      'コメント'
    ].join(',');

    const rows = results.map(result => [
      format(new Date(result.measurement_date), 'yyyy/MM/dd'),
      result.name,
      result.data.speed?.toFixed(2) || '',
      result.data.pitch?.toFixed(2) || '',
      result.data.stride?.toFixed(2) || '',
      result.data.contactTime?.toFixed(3) || '',
      result.data.flightTime?.toFixed(3) || '',
      (typeof result.data.predicted100m === 'string' ? parseFloat(result.data.predicted100m).toFixed(2) : typeof result.data.predicted100m === 'number' ? result.data.predicted100m.toFixed(2) : ''),
      `"${result.comment || ''}"`
    ].join(','));

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sprint-analysis-${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const chartData = useMemo(() => ({
    labels: results.map((result) => `${format(new Date(result.measurement_date), 'MM/dd')} ${result.name}`),
    datasets: [
      {
        label: 'スピード (m/s)',
        data: results.map((result) => (result.data.speed || 0) * 0.5),
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        order: 1,
      },
      {
        label: 'ピッチ (歩/秒)',
        data: results.map((result) => result.data.pitch || 0),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        order: 2,
      },
      {
        label: 'ストライド (m)',
        data: results.map((result) => (result.data.stride || 0) * 2),
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        order: 3,
      },
      {
        label: '接地時間×30 (秒)',
        data: results.map((result) => (result.data.contactTime || 0) * 30),
        backgroundColor: 'rgba(153, 102, 255, 0.5)',
        order: 4,
      },
      {
        label: '滞空時間×30 (秒)',
        data: results.map((result) => (result.data.flightTime || 0) * 30),
        backgroundColor: 'rgba(255, 159, 64, 0.5)',
        order: 5,
      },
    ],
  }), [results]);

  const chartOptions = useMemo<ChartOptions<'bar'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          usePointStyle: true,
        },
      },
      title: {
        display: true,
        text: '分析結果の推移',
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (label.includes('×30')) {
              return `${label}: ${(value / 30).toFixed(3)}`;
            }
            if (label === 'スピード (m/s)') {
              return `${label}: ${(value * 2).toFixed(2)}`;
            }
            if (label === 'ストライド (m)') {
              return `${label}: ${(value / 2).toFixed(2)}`;
            }
            return `${label}: ${value.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 8,
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
    },
  }), []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">データ管理</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">分析結果の記録</h2>
          <div className="text-sm text-gray-500">
            保存数: <span className={cn(
              "font-medium",
              resultCount >= MAX_RESULTS ? "text-red-600" : resultCount >= MAX_RESULTS * 0.8 ? "text-yellow-600" : "text-green-600"
            )}>
              {resultCount} / {MAX_RESULTS}
            </span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="measurementDate" className="block text-sm font-medium text-gray-700">
              測定日
            </label>
            <input
              type="date"
              id="measurementDate"
              value={measurementDate}
              onChange={(e) => setMeasurementDate(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              名前
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="distance" className="block text-sm font-medium text-gray-700">
              測定距離 (m)
            </label>
            <input
              type="number"
              id="distance"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700">
              コメント
            </label>
            <input
              type="text"
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          {saveError && (
            <div className="col-span-2 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{saveError}</h3>
                </div>
              </div>
            </div>
          )}

          <div className="col-span-2 flex justify-end">
            <button 
              type="submit" 
              className={cn(
                "btn btn-primary",
                isOverLimit && "opacity-50 cursor-not-allowed"
              )}
              disabled={isOverLimit}
            >
              記録する
            </button>
          </div>
        </form>
      </div>

      {results.length > 0 ? (
        <>
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">分析結果の推移</h2>
            </div>
            <div style={{ height: '400px' }}>
              <Bar options={chartOptions} data={chartData} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">データ一覧</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">測定日</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名前</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">距離 (m)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">スピード (m/s)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ピッチ (歩/秒)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ストライド (m)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">接地時間 (秒)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">滞空時間 (秒)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">コメント</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((result) => (
                    <tr key={result.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(result.measurement_date), 'yyyy/MM/dd')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.distance}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {result.data.speed?.toFixed(2) || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {result.data.pitch?.toFixed(2) || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {result.data.stride?.toFixed(2) || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {result.data.contactTime?.toFixed(3) || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {result.data.flightTime?.toFixed(3) || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{result.comment || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => handleDelete(result.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <ExportPanel
            results={results}
            onExportCSV={handleExportCSV}
          />
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">分析結果がまだありません。</p>
          <p className="text-gray-500">分析を実行してデータを記録してください。</p>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;